import { apiClient } from "@/services/apiClient";

export type DailyReportStatus =
  | "pending"
  | "sent"
  | "ack_ok"
  | "ack_rejected"
  | "send_failed";

export interface DailyReportListItem {
  id: string;
  fechaResumen: string;
  /** SecEnvio in the XML (DGI sequence for that calendar day). */
  secEnvio: number;
  /** nth stored send for this report date (chronological). */
  attemptNumber: number;
  status: DailyReportStatus;
  /** True when all automatic retries have been used and manual action is needed. */
  retriesExhausted: boolean;
  errorMessage: string | null;
  createdAt: string;
}

export interface ReporteDiarioSummarySection {
  cfeType: string;
  cantDocsUtil: number;
  cantDocsAnulados: number;
  cantDocsEmi: number;
}

export interface ReporteDiarioParsedSummary {
  rucEmisor?: string;
  fechaResumen?: string;
  secEnvio?: number;
  cantComprobantes: number;
  sections: ReporteDiarioSummarySection[];
}

export interface DailyReportDetail extends DailyReportListItem {
  summary: ReporteDiarioParsedSummary | null;
  /** Signed report XML sent to DGI. */
  signedXml: string | null;
  ackXml: string | null;
}

export interface ListDailyReportsResponse {
  items: DailyReportListItem[];
  total: number;
  page: number;
  limit: number;
}

export function listDailyReports(params: {
  page?: number;
  limit?: number;
}): Promise<ListDailyReportsResponse> {
  return apiClient
    .get<ListDailyReportsResponse>("/daily-reports", { params })
    .then((res) => res.data);
}

export function getDailyReport(id: string): Promise<DailyReportDetail> {
  return apiClient
    .get<DailyReportDetail>(`/daily-reports/${id}`)
    .then((res) => res.data);
}

export function resendDailyReport(id: string): Promise<DailyReportDetail> {
  return apiClient
    .post<DailyReportDetail>(`/daily-reports/${id}/resend`)
    .then((res) => res.data);
}

export function canResendDailyReport(status: DailyReportStatus): boolean {
  return status === "ack_rejected" || status === "send_failed";
}

/** Returns the user-facing display status key for i18n. */
export function dailyReportDisplayStatus(
  status: DailyReportStatus,
  retriesExhausted: boolean,
): string {
  if (
    (status === "ack_rejected" || status === "send_failed") &&
    !retriesExhausted
  ) {
    return "retrying";
  }
  if (
    (status === "ack_rejected" || status === "send_failed") &&
    retriesExhausted
  ) {
    return "needs_attention";
  }
  return status;
}
